import React, { useEffect, useMemo, useState } from "react";
import {
  SHADOW_ENTRY_BACKGROUND,
  TOOL_PREP_PREVIEW_BG,
} from "../../../../utils/editorEntrySession";
import { getTransform } from "../../../../utils/getTransform";
import {
  aspectFromSize,
  computeFittedStageSize,
  TOOL_PREP_STAGE_INSET,
  useImageNaturalSize,
} from "../../lib/toolPrepImageLayout";
import RoundLoader from "../../../../components/ui/RoundLoader";
import SoftWatermarkOverlay from "./SoftWatermarkOverlay";
import ToolPrepCanvasStage from "./ToolPrepCanvasStage";
import "./ToolPrepFlatResultStage.css";

/**
 * Flat baked-image stage for tool prep result / applying states.
 *
 * Prep canvas contract:
 * - Frame aspect is locked to the live-preview source when
 *   `frameNaturalSize` / `subjectNaturalSize` is provided.
 * - Never size the visible canvas from a bled/padded export bitmap.
 * - Soft watermark is clipped to the frame and must not affect measure.
 */
const ToolPrepFlatResultStage = ({
  className = "",
  resultImageUrl = null,
  applying = false,
  applyingLabel = "Applying…",
  loading = false,
  loadingLabel = "Loading…",
  resultAlt = "Result preview",
  emptyLabel = "Upload an image to preview",
  previewBg = TOOL_PREP_PREVIEW_BG.TRANSPARENT,
  /** Solid fill behind a white/color preview. Defaults to white. */
  stageColor = null,
  /**
   * Subject size for bleed tools (shadow/outline): frame = subject,
   * result image may extend outside with bleed.
   */
  subjectNaturalSize = null,
  /**
   * Lock frame aspect to this size (usually live-preview source/cutout).
   * Prefer this for every non-resize Apply tool.
   */
  frameNaturalSize = null,
  /** Extra pixels on each side of the baked result beyond the subject. */
  shadowBleedPx = 0,
  /**
   * Live-stage placement of the full image, as fractions of the stage.
   * Keeps a dragged subject where it was after Apply. Null recenters.
   */
  subjectPlacement = null,
  showSoftWatermark = true,
  softWatermarkToolKey = "editing",
}) => {
  const stageRef = React.useRef(null);
  const [viewport, setViewport] = useState({ width: 0, height: 0 });
  const naturalSize = useImageNaturalSize(resultImageUrl);

  const lockRequested =
    subjectNaturalSize != null || frameNaturalSize != null;

  const frameSize = useMemo(() => {
    if (subjectNaturalSize?.width > 0 && subjectNaturalSize?.height > 0) {
      return {
        width: subjectNaturalSize.width,
        height: subjectNaturalSize.height,
      };
    }
    if (frameNaturalSize?.width > 0 && frameNaturalSize?.height > 0) {
      return {
        width: frameNaturalSize.width,
        height: frameNaturalSize.height,
      };
    }
    // Lock requested but not ready yet — do not fall back to bled export size.
    if (lockRequested) {
      return { width: 0, height: 0 };
    }
    return naturalSize;
  }, [frameNaturalSize, lockRequested, naturalSize, subjectNaturalSize]);

  const fitWidth = frameSize.width;
  const fitHeight = frameSize.height;

  /**
   * Padding in source pixels (outline/shadow bleed), not export bitmap pixels.
   * The PNG is often upscaled (long edge 2000). Treating that extra resolution
   * as bleed zooms and crops the Apply preview.
   */
  const bleedPx = Math.max(0, Number(shadowBleedPx) || 0);

  const useSubjectFit =
    fitWidth > 0 &&
    fitHeight > 0 &&
    (bleedPx > 0 || subjectPlacement != null);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;

    const measure = () => {
      const width = Math.round(el.clientWidth);
      const height = Math.round(el.clientHeight);
      setViewport((prev) =>
        prev.width === width && prev.height === height
          ? prev
          : { width, height }
      );
    };

    measure();
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [resultImageUrl, fitWidth, fitHeight]);

  const stageSize = useMemo(() => {
    const vw = viewport.width;
    const vh = viewport.height;
    if (!vw || !vh) {
      return { width: 0, height: 0 };
    }
    // Empty / lock-pending: shared square stage (same as live empty previews).
    const aspect =
      fitWidth > 0 && fitHeight > 0
        ? aspectFromSize(fitWidth, fitHeight, 1)
        : 1;
    return (
      computeFittedStageSize(vw, vh, aspect, {
        inset: TOOL_PREP_STAGE_INSET,
      }) ?? { width: 0, height: 0 }
    );
  }, [fitHeight, fitWidth, viewport.height, viewport.width]);

  const resultImageStyle = useMemo(() => {
    if (!useSubjectFit || !stageSize.width || !stageSize.height || !fitWidth) {
      return undefined;
    }

    let boxX;
    let boxY;
    let boxW;
    let boxH;
    if (
      subjectPlacement &&
      subjectPlacement.width > 0 &&
      subjectPlacement.height > 0
    ) {
      boxX = subjectPlacement.x * stageSize.width;
      boxY = subjectPlacement.y * stageSize.height;
      boxW = subjectPlacement.width * stageSize.width;
      boxH = subjectPlacement.height * stageSize.height;
    } else {
      const subjectTransform = getTransform(
        fitWidth,
        fitHeight,
        stageSize.width,
        stageSize.height,
        "fit"
      );
      boxX = subjectTransform.x;
      boxY = subjectTransform.y;
      boxW = subjectTransform.width;
      boxH = subjectTransform.height;
    }
    const displayScale = boxW / fitWidth;
    const bleedY = fitHeight > 0 ? bleedPx * (boxH / fitHeight) : bleedPx * displayScale;
    const rotation = Number(subjectPlacement?.rotation) || 0;

    return {
      position: "absolute",
      width: boxW + bleedPx * displayScale * 2,
      height: boxH + bleedY * 2,
      left: boxX - bleedPx * displayScale,
      top: boxY - bleedY,
      objectFit: "fill",
      maxWidth: "none",
      maxHeight: "none",
      transform: rotation ? `rotate(${rotation}deg)` : undefined,
      transformOrigin: "center center",
    };
  }, [
    bleedPx,
    fitHeight,
    fitWidth,
    stageSize.height,
    stageSize.width,
    subjectPlacement,
    useSubjectFit,
  ]);

  const showResult = Boolean(
    resultImageUrl &&
      stageSize.width > 0 &&
      stageSize.height > 0 &&
      (!lockRequested || (fitWidth > 0 && fitHeight > 0))
  );
  const isTransparent = previewBg === TOOL_PREP_PREVIEW_BG.TRANSPARENT;
  const isOriginal = previewBg === TOOL_PREP_PREVIEW_BG.ORIGINAL;
  const isSolidPreview =
    previewBg === TOOL_PREP_PREVIEW_BG.WHITE ||
    previewBg === TOOL_PREP_PREVIEW_BG.COLOR;

  const rootClassName = ["tool-prep-flat-result", className]
    .filter(Boolean)
    .join(" ");

  return (
    <ToolPrepCanvasStage
      className={rootClassName}
      ref={stageRef}
      metaImageUrl={showResult ? resultImageUrl : null}
      metaWidth={fitWidth || undefined}
      metaHeight={fitHeight || undefined}
    >
      <div
        className={`tool-prep-flat-result__canvas${
          showResult ? " tool-prep-flat-result__canvas--result" : ""
        }${isTransparent ? " tool-prep-flat-result__canvas--checker" : ""}${
          isOriginal ? " tool-prep-flat-result__canvas--original" : ""
        }${useSubjectFit ? " tool-prep-flat-result__canvas--subject-fit" : ""}`}
        style={{
          width: stageSize.width || undefined,
          height: stageSize.height || undefined,
          backgroundColor: isSolidPreview
            ? stageColor || SHADOW_ENTRY_BACKGROUND
            : undefined,
        }}
      >
        {loading ? (
          <div className="tool-prep-flat-result__loading">
            <RoundLoader size="md" label={loadingLabel} />
          </div>
        ) : applying ? (
          <div className="tool-prep-flat-result__loading">
            <RoundLoader size="md" label={applyingLabel} />
          </div>
        ) : showResult ? (
          <>
            <img
              src={resultImageUrl}
              alt={resultAlt}
              className={`tool-prep-flat-result__image${
                useSubjectFit ? " tool-prep-flat-result__image--subject-fit" : ""
              }`}
              style={resultImageStyle}
              draggable={false}
            />
            {/* Clip SoftWatermark to the frame so the badge cannot enlarge the stage. */}
            <div className="tool-prep-flat-result__wm-clip">
              <SoftWatermarkOverlay
                visible={showSoftWatermark}
                toolKey={softWatermarkToolKey}
              />
            </div>
          </>
        ) : (
          <div className="tool-prep-flat-result__empty">{emptyLabel}</div>
        )}
      </div>
    </ToolPrepCanvasStage>
  );
};

export default ToolPrepFlatResultStage;
